import { apiClient } from "./index";

// Get return devices count statistics
export const getReturnDevicesCount = async () => {
  try {
    const response = await apiClient.get("/return-management/devices/count/");
    
    // Handle the response structure - API returns flat object with count values
    const data = response.data || {};
    
    // Normalize the response structure to match component expectations
    const normalizedData = {
      total_count: data.total_count || 0,
      active_registration: data.active_registration || 0,
      completed: data.completed || 0,
      under_repair: data.under_repair || 0,
      not_returnable: data.not_returnable || 0,
    };

    return { response: normalizedData, error: null };
  } catch (error) {
    console.error("Error fetching return devices count:", error);
    return {
      response: null,
      error: {
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch return devices count",
        data: error.response?.data,
        status: error.response?.status,
      },
    };
  }
};

// Get returns list with filters and pagination
export const getReturnsList = async (
  search = "",
  page = 1,
  pageSize = 25,
  status = "",
  dateFilter = "",
  type = ""
) => {
  try {
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    if (page) params.append("page", page.toString());
    if (pageSize) params.append("page_size", pageSize.toString());
    if (status && status !== "all") params.append("status", status);
    if (dateFilter && dateFilter !== "all") params.append("date_filter", dateFilter);
    if (type) params.append("type", type);

    const response = await apiClient.get(
      `/return-management/devices/?${params.toString()}`
    );
    return { response: response.data, error: null };
  } catch (error) {
    console.error("Error fetching returns list:", error);
    return {
      response: null,
      error: {
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch returns list",
        data: error.response?.data,
        status: error.response?.status,
      },
    };
  }
};

// Create return
export const createReturn = async (returnData) => {
  try {
    const response = await apiClient.post(
      "/return-management/devices/",
      returnData
    );
    return { response: response.data, error: null };
  } catch (error) {
    console.error("Error creating return:", error);
    const errorResponse = error.response?.data || {};
    
    // Extract error message from backend response
    let errorMessage = 
      errorResponse.message ||
      errorResponse.error ||
      errorResponse.detail ||
      error.message ||
      "Failed to create return";
    
    // Handle validation errors
    if (errorResponse.errors || errorResponse.non_field_errors) {
      const validationErrors = errorResponse.errors || errorResponse.non_field_errors;
      if (Array.isArray(validationErrors)) {
        errorMessage = validationErrors.join(", ");
      } else if (typeof validationErrors === "object") {
        errorMessage = Object.entries(validationErrors)
          .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(", ") : value}`)
          .join(", ");
      } else {
        errorMessage = validationErrors;
      }
    }
    
    return {
      response: null,
      error: {
        message: errorMessage,
        data: errorResponse,
        status: error.response?.status,
      },
    };
  }
};

// Get return by ID
export const getReturnById = async (returnId) => {
  try {
    const response = await apiClient.get(
      `/return-management/devices/${returnId}/`
    );
    return { response: response.data, error: null };
  } catch (error) {
    console.error("Error fetching return by ID:", error);
    return {
      response: null,
      error: {
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch return",
        data: error.response?.data,
        status: error.response?.status,
      },
    };
  }
};

// Update return (PATCH)
export const updateReturn = async (returnId, returnData) => {
  try {
    const response = await apiClient.patch(
      `/return-management/devices/${returnId}/`,
      returnData
    );
    return { response: response.data, error: null };
  } catch (error) {
    console.error("Error updating return:", error);
    const errorResponse = error.response?.data || {};
    
    // Extract error message from backend response
    let errorMessage = 
      errorResponse.message ||
      errorResponse.error ||
      errorResponse.detail ||
      error.message ||
      "Failed to update return";
    
    // Handle validation errors
    if (errorResponse.errors || errorResponse.non_field_errors) {
      const validationErrors = errorResponse.errors || errorResponse.non_field_errors;
      if (Array.isArray(validationErrors)) {
        errorMessage = validationErrors.join(", ");
      } else if (typeof validationErrors === "object") {
        errorMessage = Object.entries(validationErrors)
          .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(", ") : value}`)
          .join(", ");
      } else {
        errorMessage = validationErrors;
      }
    }
    
    return {
      response: null,
      error: {
        message: errorMessage,
        data: errorResponse,
        status: error.response?.status,
      },
    };
  }
};

// Delete return
export const deleteReturn = async (returnId) => {
  try {
    const response = await apiClient.delete(
      `/return-management/devices/${returnId}/`
    );
    return { response: response.data, error: null };
  } catch (error) {
    console.error("Error deleting return:", error);
    const errorResponse = error.response?.data || {};
    
    // Extract error message from backend response
    let errorMessage = 
      errorResponse.message ||
      errorResponse.error ||
      errorResponse.detail ||
      error.message ||
      "Failed to delete return";
    
    // Handle validation errors
    if (errorResponse.errors || errorResponse.non_field_errors) {
      const validationErrors = errorResponse.errors || errorResponse.non_field_errors;
      if (Array.isArray(validationErrors)) {
        errorMessage = validationErrors.join(", ");
      } else if (typeof validationErrors === "object") {
        errorMessage = Object.entries(validationErrors)
          .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(", ") : value}`)
          .join(", ");
      } else {
        errorMessage = validationErrors;
      }
    }
    
    return {
      response: null,
      error: {
        message: errorMessage,
        data: errorResponse,
        status: error.response?.status,
      },
    };
  }
};

// Update return status
export const updateReturnStatus = async (returnId, status) => {
  try {
    const response = await apiClient.patch(
      `/return-management/devices/${returnId}/status/`,
      { status }
    );
    return { response: response.data, error: null };
  } catch (error) {
    console.error("Error updating return status:", error);
    const errorResponse = error.response?.data || {};
    
    // Extract error message from backend response
    let errorMessage = 
      errorResponse.message ||
      errorResponse.error ||
      errorResponse.detail ||
      error.message ||
      "Failed to update return status";
    
    // Handle validation errors
    if (errorResponse.errors || errorResponse.non_field_errors) {
      const validationErrors = errorResponse.errors || errorResponse.non_field_errors;
      if (Array.isArray(validationErrors)) {
        errorMessage = validationErrors.join(", ");
      } else if (typeof validationErrors === "object") {
        errorMessage = Object.entries(validationErrors)
          .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(", ") : value}`)
          .join(", ");
      } else {
        errorMessage = validationErrors;
      }
    }
    
    return {
      response: null,
      error: {
        message: errorMessage,
        data: errorResponse,
        status: error.response?.status,
      },
    };
  }
};

